"use client";

import React, { useRef, useState } from "react";
import ImageComponent from "../common/ImageComponent";
import styles from "./cart.module.scss";

const getHighRes = (url, imgRes) =>
  url
    ?.replace?.("r200x200", `r${imgRes}x${imgRes}`)
    ?.replace?.("v200x200", `v${imgRes}x${imgRes}`);

const TicketConfirmation = ({ ticket, event, formatDateTime, formatAmount }) => {
  const ticketItems = ticket?.tickets || [];
  const ticketRef = useRef(null);
  const [isDownloadingTicket, setIsDownloadingTicket] = useState(false);

  const handleDownloadTicket = async () => {
    if (!ticketRef.current || isDownloadingTicket) return;

    setIsDownloadingTicket(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(ticketRef.current, {
        backgroundColor: "#ffffff",
        scale: window.devicePixelRatio > 1 ? 2 : 1,
        useCORS: true,
      });
      const imageUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = `ticket-${ticket?.checkSum || ticket?.ticketId || "download"}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      setIsDownloadingTicket(false);
    }
  };

  if (!ticket) return null;

  return (
    <>
      <div className={styles.ticketConfirmation} ref={ticketRef}>
        <div className={styles.ticketConfirmationHeader}>
          <div>
            <span className={styles.ticketConfirmationLabel}>Ticket</span>
            <h2>{ticket.eventName || event?.name || "Event Ticket"}</h2>
          </div>
          {ticket.status && (
            <span className={styles.ticketStatus}>{ticket.status}</span>
          )}
        </div>

        <div className={styles.ticketConfirmationGrid}>
          <div>
            <span>Name</span>
            <strong>
              {[ticket.firstName, ticket.lastName].filter(Boolean).join(" ") ||
                ticket.userName ||
                "N/A"}
            </strong>
          </div>
          <div>
            <span>Email</span>
            <strong>{ticket.email || "N/A"}</strong>
          </div>
          <div>
            <span>Phone</span>
            <strong>
              {[ticket.intCode, ticket.phone].filter(Boolean).join(" ") ||
                "N/A"}
            </strong>
          </div>
          <div>
            <span>Ticket ID</span>
            <strong>{ticket.checkSum || ticket.ticketId || "N/A"}</strong>
          </div>
          <div>
            <span>Date</span>
            <strong>
              {formatDateTime(
                ticket.startDate,
                ticket.startTime,
                ticket.timezone,
              )}
            </strong>
          </div>
          <div>
            <span>Amount Paid</span>
            <strong>{formatAmount(ticket.amountPaid, ticket.currency)}</strong>
          </div>
        </div>

        {ticketItems.length > 0 && (
          <div className={styles.ticketList}>
            {ticketItems.map((item) => (
              <div className={styles.ticketCard} key={item._id || item.ticketNo}>
                <div className={styles.ticketCardInfo}>
                  <div>
                    <span>Ticket Type</span>
                    <strong>{item.name || "Ticket"}</strong>
                  </div>
                  <div>
                    <span>Ticket No</span>
                    <strong>{item.ticketNo || "N/A"}</strong>
                  </div>
                  <div>
                    <span>Transaction No</span>
                    <strong>{item.transactionNo || "N/A"}</strong>
                  </div>
                  <div>
                    <span>Price</span>
                    <strong>{formatAmount(item.amount, item.currency)}</strong>
                  </div>
                </div>
                {item.barCodeUrl && (
                  <div className={styles.ticketBarcode}>
                    <ImageComponent
                      src={getHighRes(item.barCodeUrl, 300)}
                      alt={`Barcode for ticket ${item.ticketNo || ""}`}
                      width={260}
                      height={120}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        className={styles.ticketDownloadButton}
        onClick={handleDownloadTicket}
        disabled={isDownloadingTicket}
      >
        {isDownloadingTicket ? "Preparing Download..." : "Download Ticket"}
      </button>
    </>
  );
};

export default TicketConfirmation;
